import { useOutletContext, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { buildPath } from "@/lib/routes";
import { useLang } from "@/hooks/useLang";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Stagger, StaggerItem } from "@/components/motion/Reveal";
import LeadMagnet from "@/components/LeadMagnet";
import { breadcrumbSchema } from "@/lib/schema";

export default function TechStacksHub() {
  const { lang, t } = useLang();
  const { profile } = useOutletContext();
  const { data: stacks = [] } = useQuery({ queryKey: ["stacks"], queryFn: api.getTechStacks });

  const meta = {
    da: { title: "Tech Stacks | Værktøjerne jeg bruger | Magnus Løv", h: ["Min tech stack", "— værktøj for værktøj"], d: "De værktøjer jeg faktisk bruger hver dag inden for SEO, marketing, sociale medier, content og produktivitet. Ærlige anbefalinger — nogle links er affiliate." },
    en: { title: "Tech Stacks | The tools I use | Magnus Løv", h: ["My tech stack", "— tool by tool"], d: "The tools I actually use every day across SEO, marketing, social media, content and productivity. Honest recommendations — some links are affiliate." },
  }[lang];

  return (
    <>
      <Seo routeKey="techstacks" lang={lang} title={meta.title} description={meta.d}
        jsonLd={[breadcrumbSchema([{ name: t("nav_home"), path: buildPath("home", lang) }, { name: t("nav_techstacks"), path: buildPath("techstacks", lang) }])]} />
      <PageHero title={meta.h} description={meta.d}
        crumbs={[{ label: t("nav_home"), to: "home" }, { label: t("nav_techstacks") }]} />

      <section data-testid="hub-intro" className="px-6 md:px-12 lg:px-20 pt-12 -mb-4">
        <div className="max-w-3xl space-y-4 text-muted-foreground font-light leading-relaxed">
          <p>{lang === "da"
            ? "Et godt værktøj gør ikke en god SEO'er — men de rigtige værktøjer gør en god SEO'er hurtigere og mere præcis. Nedenfor har jeg samlet min faktiske stack, opdelt efter formål, så du kan se præcis, hvad jeg bruger til hvad."
            : "A good tool doesn't make a good SEO — but the right tools make a good SEO faster and more precise. Below I've gathered my actual stack, split by purpose, so you can see exactly what I use for what."}</p>
          <p>{lang === "da"
            ? "Jeg anbefaler kun værktøjer, jeg selv bruger og står inde for. Nogle links er affiliate-links, hvilket betyder, at jeg kan tjene en lille kommission uden ekstra omkostning for dig."
            : "I only recommend tools I use and stand behind myself. Some links are affiliate links, meaning I may earn a small commission at no extra cost to you."}</p>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-16">
        <Stagger className="grid gap-6 md:grid-cols-2">
          {stacks.map((s, i) => {
            const c = s[lang] || s.da;
            return (
              <StaggerItem key={s.slug}>
                <Link to={buildPath("techstack", lang, { slug: s.slug })} data-testid={`stack-card-${s.slug}`}
                  className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card p-8 transition-all duration-500 hover:border-primary hover:shadow-[0_0_50px_-12px_hsl(var(--primary)/0.4)]">
                  <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-primary/10 blur-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-muted-foreground">STACK 0{i + 1}</span>
                    <ArrowUpRight size={22} className="text-muted-foreground transition-all group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                  <h2 className="mt-6 font-heading text-2xl md:text-3xl font-extrabold tracking-tight">{c.title}</h2>
                  <p className="mt-3 flex-1 text-sm text-muted-foreground font-light leading-relaxed">{c.tagline}</p>
                  <div className="mt-6 flex items-center gap-2">
                    <span className="rounded-full border border-border px-3 py-1 font-mono text-xs text-primary">{s.tools?.length} {t("tools_count")}</span>
                  </div>
                </Link>
              </StaggerItem>
            );
          })}
        </Stagger>
      </section>
      <LeadMagnet profile={profile} />
    </>
  );
}
